import {
  FcArea,
  FcCenterText,
  FcColor,
  FcColorBlack,
  FcColorGradient,
  FcColorTransparent,
  FcColorType,
  FcContainer,
  FcPositionProperty,
  FcReservedArea,
  FcShape,
  FcSvg,
  FcText,
  fcBuildSvgColor,
  fcBuildSvgColorForImageFill,
  fcContainerIsCustom,
  fcGetColorLabel,
  getRimIndent
} from '@dtx-company/flowcode-generator-types/src'
import { fcChangeSvgColor, fcGetSvgDimensions, fcImageAddSize, fcImageToTag } from './fc_images'
import { fcGenerateGradient } from './fc_gradient'
import { fcGetDomId } from './fc_dom'
import { fcRenderCenterText, fcRenderCircle, fcRenderGetUnusedPoints } from './fc_fonts'
import { roundDecimal } from './fc_utils'

class FcRimPatternData {
  shape: FcShape
  size = 0
  imageUrls: string[] = []
  colors: FcColor[] = []
  fillSvg = false
  useCmyk = false
  colorIndex = 0
  imageIndex = 0
  imageData: string[] = []
  imagesCreated: Map<string, boolean> = new Map<string, boolean>()
  idBase = ''

  constructor(container: FcContainer) {
    this.shape = container.rimPatternShape || FcShape.CIRCLE
    this.size = container.rimPatternSize!
    if (container.rimPatternImageUrl) {
      this.imageUrls = container.rimPatternImageUrl.split(',')
    }
    if (container.rimPatternColor) {
      this.colors = container.rimPatternColor.split(',')
    }
    this.fillSvg = container.rimPatternColorFillSvg || false
    this.useCmyk = container.useCmyk
    this.idBase = fcGetDomId()
  }

  async loadImages(): Promise<void> {
    const len = this.imageUrls.length
    for (let i = 0; i < len; i++) {
      this.imageData[i] = await fcImageToTag(this.useCmyk, this.imageUrls[i])
    }
    if (len) {
      this.shape = FcShape.CUSTOM
    }
  }

  getNextColor(): FcColor {
    if (this.colors.length === 0) {
      return FcColorBlack
    }
    const col = this.colors[this.colorIndex]
    this.colorIndex++
    if (this.colorIndex >= this.colors.length) {
      this.colorIndex = 0
    }
    return col
  }

  getNextImageDataInd(): number {
    if (this.imageData.length === 0) {
      return 0
    }
    const ind = this.imageIndex
    this.imageIndex++
    if (this.imageIndex >= this.imageData.length) {
      this.imageIndex = 0
    }
    return ind
  }
}

export async function fcRenderContainer(
  container: FcContainer,
  svg: FcSvg,
  reserved: FcReservedArea,
  gridBg: string,
  noRenderContainerText?: boolean
): Promise<void> {
  const idBase = fcGetDomId()

  const borderIntersections = container.borderIntersections
  if (borderIntersections && borderIntersections.length > 0) {
    const w = svg.width
    const len = borderIntersections.length
    const borderInset = container.borderInsetCalcValue
    const borderSize = container.borderSize
    const radius = w / 2 - borderSize / 2 - borderInset
    const halfBorder = borderSize / 2
    for (let i = 0; i < len; i++) {
      const inter = borderIntersections[i]
      const interLen = inter.length
      for (let j = 0; j < interLen; j++) {
        const rads = inter[j]
        const startX = w / 2 + radius * Math.cos(rads)
        const startY = w / 2 + radius * Math.sin(rads)
        reserved.addBox(startX - halfBorder, startY - halfBorder, borderSize, borderSize)
      }
    }
  }

  if (container.dataCheckOnly) {
    return
  }
  if (
    (container.fragments || container.containerImageUrl) &&
    !container.containerImageBackgroundOnly
  ) {
    if (container.shape === FcShape.SQUARE) {
      addOuterShapeBackground(
        container.useCmyk,
        false,
        idBase,
        svg,
        container.shape,
        container.backgroundColor,
        container.borderSize,
        container.containerSquareRadius,
        undefined,
        container.borderInsetCalcValue,
        false,
        true
      )
    }
    await renderContainerImage(svg, idBase, container)
    if (gridBg) {
      svg.addToDom(gridBg)
    }

    await fcRenderContainerText(
      svg,
      idBase,
      container,
      reserved,
      noRenderContainerText,
      container.containerImageOverlayPrivacy ? false : true
    )

    if (container.fragmentRadiusMask) {
      const w = svg.width
      const h = svg.height
      const r = (svg.width * container.fragmentRadiusMask) / 200
      let element = `<circle id="${idBase}containerShape" cx="${roundDecimal(
        w / 2
      )}" cy="${roundDecimal(h / 2)}" r="${roundDecimal(r)}" ${fcBuildSvgColor(
        '#ffff0000',
        FcColorType.STROKE,
        container.useCmyk
      )} stroke-width="0" ${fcBuildSvgColor('#ffff0000', FcColorType.FILL, container.useCmyk)} />`
      element += fcAddClipPath(idBase)
      svg.setClip(idBase)
      svg.addToDom(element)
    }
  } else {
    addOuterShapeBackground(
      container.useCmyk,
      false,
      idBase,
      svg,
      container.shape,
      container.backgroundColor,
      container.borderSize,
      container.containerSquareRadius,
      undefined,
      container.borderInsetCalcValue,
      true
    )
    await renderContainerImage(svg, idBase, container)
    if (gridBg) {
      svg.addToDom(gridBg)
    }

    await fcRenderContainerText(svg, idBase, container, reserved, noRenderContainerText)
  }
}

export async function fcRenderContainerOverlay(
  container: FcContainer,
  svg: FcSvg,
  reserved: FcReservedArea
): Promise<void> {
  if (container.dataCheckOnly) {
    return
  }
  const idBase = fcGetDomId()
  if (!fcContainerIsCustom(container)) {
    addOuterShapeBorder(
      container.useCmyk,
      idBase,
      svg,
      container.shape,
      container.borderColor,
      container.borderSize,
      container.containerSquareRadius,
      container.borderGradient,
      container.borderOutlineColor,
      container.borderOutlineSize,
      container.borderInsetCalcValue,
      container.borderIntersections
    )
  }
  if (container.containerImageUrlFg) {
    let opacity = container.containerImageUrlFgOpacity
    if (!opacity) {
      opacity = 1
    }
    let color = ''
    if (container.containerImageUrlFillSvg) {
      color = fcBuildSvgColorForImageFill(container.borderColor, container.useCmyk)
    }
    const imageData = await fcImageToTag(container.useCmyk, container.containerImageUrlFg, color)
    const containerImg = fcImageAddSize(imageData, 0, 0, svg.width, svg.height, '', opacity)
    svg.addToDom(containerImg)
  }
}

export async function fcRenderContainerInsetBackground(
  container: FcContainer,
  svg: FcSvg
): Promise<void> {
  if (container.dataCheckOnly) {
    return
  }
  const w = svg.width
  const h = svg.height

  let element = ''
  const backgroundColor = container.backgroundColor
  switch (container.shape) {
    // only circle supported for now
    case FcShape.CIRCLE:
    default:
      element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(h / 2)}" r="${roundDecimal(
        w / 2
      )}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.STROKE,
        container.useCmyk
      )} stroke-width="0" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        container.useCmyk
      )} />`
      break
  }
  svg.addToDom(element)
}

export async function fcRenderContainerRim(container: FcContainer, svg: FcSvg): Promise<void> {
  if (container.dataCheckOnly) {
    return
  }
  const idBase = fcGetDomId()
  const rimIndent = getRimIndent(container)
  addOuterShapeBackground(
    container.useCmyk,
    true,
    idBase,
    svg,
    container.shape,
    container.rimBackgroundColor || FcColorTransparent,
    container.rimBorderSize!,
    container.containerSquareRadius,
    rimIndent + container.borderSize,
    container.borderInsetCalcValue,
    false
  )
  addOuterShapeRimBorder(
    container.useCmyk,
    `rim-${idBase}`,
    svg,
    container.shape,
    container.rimBorderColor || FcColorTransparent,
    container.rimBorderSize!,
    container.containerSquareRadius,
    container.rimBorderGradient,
    container.rimOutlineColor,
    container.rimOutlineSize,
    false
  )

  if (container.rimWidth === 0) {
    return
  }

  let opacity = container.rimImageUrlOpacity
  if (!opacity) {
    opacity = 1
  }

  if (container.rimImageUrl) {
    const imageData = await fcImageToTag(container.useCmyk, container.rimImageUrl)
    const containerImg = fcImageAddSize(imageData, 0, 0, svg.width, svg.height, '', opacity)
    svg.addToDom(containerImg)
  }

  const reserved = new FcReservedArea()
  const offset = container.rimBorderSize! + container.rimWidth! / 2
  if (container.rimText) {
    let i = 0
    for (const text of container.rimText) {
      const name = `contrimtext-${i}-${idBase}`
      await renderTextRim(name, svg, text, container, reserved, offset)
      i++
    }
  }

  if (container.rimPatternSize && (container.rimPatternShape || container.rimPatternImageUrl)) {
    const rimPatternData = new FcRimPatternData(container)
    await rimPatternData.loadImages()

    const points = fcRenderGetUnusedPoints(
      svg,
      svg.width / 2,
      svg.height / 2,
      svg.width / 2 - offset,
      (container.rimPatternSpacing || 0) + container.rimPatternSize,
      reserved
    )
    let str = ''
    points.forEach(point => {
      str += getRimShape(container.useCmyk, point.x, point.y, point.angle, rimPatternData)
    })
    svg.addToDom(str)
  }
}

function getRimShape(
  useCmyk: boolean,
  x: number,
  y: number,
  angle: number,
  patternData: FcRimPatternData
): string {
  let modrect = ''
  const color = patternData.getNextColor()
  const shape = patternData.shape

  switch (shape) {
    case FcShape.CIRCLE:
      modrect = `<circle cx="${roundDecimal(x)}" cy="${roundDecimal(y)}" r="${roundDecimal(
        patternData.size / 2
      )}" ${fcBuildSvgColor(color, FcColorType.FILL, useCmyk)} />`
      break
    case FcShape.DIAMOND:
      modrect = `<rect x="${roundDecimal(x - patternData.size / 2)}" y="${roundDecimal(
        y - patternData.size / 2
      )}" width="${roundDecimal(patternData.size)}" height="${roundDecimal(
        patternData.size
      )}" ${fcBuildSvgColor(color, FcColorType.FILL, useCmyk)} transform="rotate(${
        45 + angle
      } ${x} ${y})"/>`
      break
    case FcShape.CUSTOM: {
      let col = ''
      if (patternData.fillSvg && color) {
        col = fcGetColorLabel(color)
      }
      const ind = patternData.getNextImageDataInd()
      const imgId = `rimg-${ind}-${col}-${patternData.idBase}`.replace(/\./g, '')
      if (!patternData.imagesCreated.get(imgId)) {
        patternData.imagesCreated.set(imgId, true)
        let data = patternData.imageData[ind]
        if (col) {
          data = fcChangeSvgColor(data, fcBuildSvgColorForImageFill(color, useCmyk))
        }
        const sizedData = fcImageAddSize(data, 0, 0, patternData.size, patternData.size, imgId)
        modrect = `<defs>${sizedData}</defs>`
      }
      modrect += `<use x="${roundDecimal(x - patternData.size / 2)}" y="${roundDecimal(
        y - patternData.size / 2
      )}" xlink:href="#${imgId}" transform="rotate(${roundDecimal(angle)} ${roundDecimal(
        x
      )} ${roundDecimal(y)})"/>`
      break
    }
    case FcShape.SQUARE:
    default:
      modrect = `<rect x="${roundDecimal(x - patternData.size / 2)}" y="${roundDecimal(
        y - patternData.size / 2
      )}" width="${roundDecimal(patternData.size)}" height="${roundDecimal(
        patternData.size
      )}" ${fcBuildSvgColor(
        color,
        FcColorType.FILL,
        useCmyk
      )} shape-rendering="crispEdges" transform="rotate(${roundDecimal(angle)} ${roundDecimal(
        x
      )} ${roundDecimal(y)})"/>`
      break
  }

  return modrect
}

async function renderText(
  name: string,
  svg: FcSvg,
  text: FcText,
  containerShape: FcShape,
  reserved: FcReservedArea,
  borderSize: number,
  inset: number,
  privacyTextPaddingHorizontal?: number,
  privacyTextPaddingVertical?: number,
  noRender?: boolean
): Promise<void> {
  let shape = containerShape
  if (text.shape !== undefined) {
    shape = text.shape
  }

  switch (shape) {
    case FcShape.SQUARE:
      const offset = (text.position.offsetPercentage * svg.height) / 100 + borderSize
      const centerText: FcCenterText = {
        text: [text],
        paddingHorizontal: privacyTextPaddingHorizontal || 0,
        paddingVertical: privacyTextPaddingVertical || 0,
        heightOffset:
          text.position.property === FcPositionProperty.TOP ? svg.height - offset : offset
      }
      await fcRenderCenterText(centerText, svg, reserved, noRender)
      break
    case FcShape.CIRCLE:
      await fcRenderCircle(
        name,
        svg,
        text,
        svg.width / 2,
        svg.height / 2,
        svg.width / 2 - (text.position.offsetPercentage * svg.width) / 100 + borderSize - 2 - inset,
        reserved,
        false,
        privacyTextPaddingHorizontal,
        privacyTextPaddingVertical,
        noRender
      )
      break
    default:
      break
  }
}

async function renderTextRim(
  name: string,
  svg: FcSvg,
  text: FcText,
  container: FcContainer,
  reserved: FcReservedArea,
  offset: number
): Promise<void> {
  switch (container.shape) {
    case FcShape.SQUARE:
      const positionOffset = (text.position.offsetPercentage * svg.height) / 100
      const heightOffset =
        (text.position.property === FcPositionProperty.TOP ? svg.height - offset : offset) -
        positionOffset
      const centerText: FcCenterText = {
        text: [text],
        paddingHorizontal: 0,
        paddingVertical: 0,
        heightOffset: heightOffset
      }
      await fcRenderCenterText(centerText, svg, reserved)
      break
    case FcShape.CIRCLE:
      await fcRenderCircle(
        name,
        svg,
        text,
        svg.width / 2,
        svg.height / 2,
        svg.width / 2 - offset,
        reserved,
        true
      )
      break
    default:
      break
  }
}

function addOuterShapeBackground(
  useCmyk: boolean,
  isRim: boolean,
  idBase: string,
  svg: FcSvg,
  containerShape: FcShape,
  backgroundColor: FcColor,
  borderSize: number,
  containerSquareRadius: number,
  maskWidth?: number,
  borderInset?: number,
  setClip?: boolean,
  hasContainerImage?: boolean
): void {
  const w = svg.width
  const h = svg.height

  if (!borderSize && !hasContainerImage) {
    return
  }

  let rxDiff = 0
  if (isRim && containerShape === FcShape.SQUARE) {
    if (containerSquareRadius !== 0) {
      rxDiff = (Math.sqrt(borderSize * borderSize * 2) - Math.sqrt(1 * 1 * 2)) / 3
    }
    borderSize = 0
  } else if (containerShape === FcShape.SQUARE && hasContainerImage) {
    rxDiff = 4
  }

  if (borderInset) {
    borderInset += borderSize
  } else {
    borderInset = 0
  }
  const diamondWidth = w / Math.sqrt(2) - borderSize
  const diamondOffset = (w - diamondWidth) / 2

  let element = ''
  switch (containerShape) {
    case FcShape.SQUARE:
      element = `<rect rx="${roundDecimal(
        containerSquareRadius + rxDiff
      )}" id="${idBase}containerShape" x="${roundDecimal(borderSize / 2)}" y="${roundDecimal(
        borderSize / 2
      )}" width="${roundDecimal(w - borderSize)}" height="${roundDecimal(
        h - borderSize
      )}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${roundDecimal(borderSize)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )}/>`
      break
    case FcShape.DIAMOND:
      element = `<rect id="${idBase}containerShape" x="${roundDecimal(
        diamondOffset
      )}" y="${roundDecimal(diamondOffset)}" width="${roundDecimal(
        diamondWidth
      )}" height="${roundDecimal(diamondWidth)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )} ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${borderSize}" transform="rotate(45 ${roundDecimal(
        diamondOffset + diamondWidth / 2
      )} ${roundDecimal(diamondOffset + diamondWidth / 2)})" />`
      break
    case FcShape.NONE:
      break
    case FcShape.CIRCLE:
    default:
      let maskStr = ''
      if (maskWidth) {
        element += `<mask id="${idBase}-contmask"><circle cx="${roundDecimal(
          w / 2
        )}" fill="#ffffff" cy="${roundDecimal(h / 2)}" r="${roundDecimal(
          w / 2 - borderInset
        )}"/><circle cx="${roundDecimal(w / 2)}" fill="#000000" cy="${roundDecimal(
          h / 2
        )}" r="${roundDecimal(w / 2 - maskWidth - borderInset)}"/></mask>`
        maskStr = `mask="url(#${idBase}-contmask)"`
      }
      element += `<circle ${maskStr} id="${idBase}containerShape" cx="${roundDecimal(
        w / 2
      )}" cy="${roundDecimal(h / 2)}" r="${roundDecimal(w / 2 - borderInset)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="0" ${fcBuildSvgColor(backgroundColor, FcColorType.FILL, useCmyk)} />`
      break
  }
  if (setClip) {
    element += fcAddClipPath(idBase)
    svg.setClip(idBase)
  }
  svg.addToDom(element)
}

function addOuterShapeRimBorder(
  useCmyk: boolean,
  idBase: string,
  svg: FcSvg,
  containerShape: FcShape,
  rimBorderColor: FcColor,
  rimBorderSize: number,
  containerSquareRadius: number,
  rimBorderGradient?: FcColorGradient,
  outlineColor?: FcColor,
  outlineWidth?: number,
  setClip?: boolean
): void {
  if (!rimBorderSize) {
    return
  }
  const w = svg.width
  const h = svg.height

  const diamondWidth = w / Math.sqrt(2) - rimBorderSize
  const diamondOffset = (w - diamondWidth) / 2
  const backgroundColor = '#ffffff00'

  if (rimBorderGradient) {
    const label = idBase + 'rimGradient'
    const gradientDef = fcGenerateGradient(useCmyk, rimBorderGradient, label)
    svg.addToDom(gradientDef)
    rimBorderColor = `url(#${label})`
  }

  let element = ''
  switch (containerShape) {
    case FcShape.SQUARE:
      element = `<rect rx="${roundDecimal(containerSquareRadius)}" x="${roundDecimal(
        rimBorderSize / 2
      )}" y="${roundDecimal(rimBorderSize / 2)}" width="${roundDecimal(
        w - rimBorderSize
      )}" height="${roundDecimal(h - rimBorderSize)}" ${fcBuildSvgColor(
        rimBorderColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${roundDecimal(rimBorderSize)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )}/>`
      if (outlineColor && outlineWidth) {
        let rxDiff = 0
        if (containerSquareRadius !== 0) {
          rxDiff =
            (Math.sqrt(rimBorderSize * rimBorderSize * 2) -
              Math.sqrt(outlineWidth * outlineWidth * 2)) /
            3
        }
        element += `<rect rx="${roundDecimal(containerSquareRadius + rxDiff)}" x="${roundDecimal(
          outlineWidth / 2
        )}" y="${roundDecimal(outlineWidth / 2)}" width="${roundDecimal(
          w - outlineWidth
        )}" height="${roundDecimal(h - outlineWidth)}" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${outlineWidth}" fill="none"/>`
        element += `<rect rx="${roundDecimal(containerSquareRadius - rxDiff)}" x="${roundDecimal(
          rimBorderSize - outlineWidth / 2
        )}" y="${roundDecimal(rimBorderSize - outlineWidth / 2)}" width="${roundDecimal(
          w - rimBorderSize * 2 + outlineWidth
        )}" height="${roundDecimal(w - rimBorderSize * 2 + outlineWidth)}" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
      }
      break
    case FcShape.DIAMOND:
      element = `<rect id="${idBase}containerShape" x="${roundDecimal(
        diamondOffset
      )}" y="${roundDecimal(diamondOffset)}" width="${roundDecimal(
        diamondWidth
      )}" height="${roundDecimal(diamondWidth)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )} ${fcBuildSvgColor(
        rimBorderColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${roundDecimal(rimBorderSize)}" transform="rotate(45 ${roundDecimal(
        diamondOffset + diamondWidth / 2
      )} ${roundDecimal(diamondOffset + diamondWidth / 2)})" />`
      if (outlineColor && outlineWidth) {
        element += `<rect x="${roundDecimal(diamondOffset + outlineWidth / 2)}" y="${roundDecimal(
          diamondOffset + outlineWidth / 2
        )}" width="${roundDecimal(diamondWidth - outlineWidth)}" height="${roundDecimal(
          diamondWidth - outlineWidth
        )}" fill="none" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${outlineWidth}" transform="rotate(45 ${roundDecimal(
          diamondOffset + diamondWidth / 2
        )} ${roundDecimal(diamondOffset + diamondWidth / 2)})" />`
        element += `<rect x="${roundDecimal(
          diamondOffset + rimBorderSize / 2 - outlineWidth / 2
        )}" y="${roundDecimal(
          diamondOffset + rimBorderSize / 2 - outlineWidth / 2
        )}" width="${roundDecimal(
          diamondWidth - rimBorderSize + outlineWidth
        )}" height="${roundDecimal(
          diamondWidth - rimBorderSize + outlineWidth
        )}" fill="none" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${outlineWidth}" transform="rotate(45 ${roundDecimal(
          diamondOffset + diamondWidth / 2
        )} ${roundDecimal(diamondOffset + diamondWidth / 2)})" />`
      }
      break
    case FcShape.CIRCLE:
    default:
      element = `<circle id="${idBase}containerShape" cx="${roundDecimal(
        w / 2
      )}" cy="${roundDecimal(h / 2)}" r="${roundDecimal(
        w / 2 - rimBorderSize / 2
      )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
        rimBorderColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${roundDecimal(rimBorderSize)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )}/>`
      if (outlineColor && outlineWidth) {
        element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(
          h / 2
        )}" r="${roundDecimal(
          w / 2 - outlineWidth / 2
        )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
        element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(
          h / 2
        )}" r="${roundDecimal(
          w / 2 - rimBorderSize + outlineWidth / 2
        )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
      }
      break
  }
  if (setClip) {
    element += fcAddClipPath(idBase)
    svg.setClip(idBase)
  }
  svg.addToDom(element)
}

function addOuterShapeBorder(
  useCmyk: boolean,
  idBase: string,
  svg: FcSvg,
  containerShape: FcShape,
  borderColor: FcColor,
  borderSize: number,
  containerSquareRadius: number,
  borderGradient?: FcColorGradient,
  outlineColor?: FcColor,
  outlineWidth?: number,
  borderInset?: number,
  borderIntersections?: number[][]
): void {
  const w = svg.width
  const h = svg.height

  if (!borderInset) {
    borderInset = 0
  }
  const diamondWidth = w / Math.sqrt(2) - borderSize
  const diamondOffset = (w - diamondWidth) / 2
  const backgroundColor = '#ffffff00'

  if (borderGradient) {
    const label = idBase + 'borderGradient'
    const gradientDef = fcGenerateGradient(useCmyk, borderGradient, label)
    svg.addToDom(gradientDef)
    borderColor = `url(#${label})`
  }

  let element = ''
  switch (containerShape) {
    case FcShape.SQUARE:
      element = `<rect rx="${roundDecimal(containerSquareRadius)}" x="${roundDecimal(
        borderSize / 2
      )}" y="${roundDecimal(borderSize / 2)}" width="${roundDecimal(
        w - borderSize
      )}" height="${roundDecimal(h - borderSize)}" ${fcBuildSvgColor(
        borderColor,
        FcColorType.STROKE,
        useCmyk
      )} stroke-width="${roundDecimal(borderSize)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )}/>`
      if (outlineColor && outlineWidth) {
        let rxDiff = 0
        if (containerSquareRadius !== 0) {
          rxDiff =
            (Math.sqrt(borderSize * borderSize * 2) - Math.sqrt(outlineWidth * outlineWidth * 2)) /
            3
        }
        element += `<rect rx="${roundDecimal(containerSquareRadius + rxDiff)}" x="${roundDecimal(
          outlineWidth / 2
        )}" y="${roundDecimal(outlineWidth / 2)}" width="${roundDecimal(
          w - outlineWidth
        )}" height="${roundDecimal(h - outlineWidth)}" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
        element += `<rect rx="${roundDecimal(containerSquareRadius - rxDiff)}" x="${roundDecimal(
          borderSize - outlineWidth / 2
        )}" y="${roundDecimal(borderSize - outlineWidth / 2)}" width="${roundDecimal(
          w - borderSize * 2 + outlineWidth
        )}" height="${roundDecimal(w - borderSize * 2 + outlineWidth)}" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
      }
      break
    case FcShape.DIAMOND:
      element = `<rect id="${idBase}containerShape" x="${roundDecimal(
        diamondOffset
      )}" y="${roundDecimal(diamondOffset)}" width="${roundDecimal(
        diamondWidth
      )}" height="${roundDecimal(diamondWidth)}" ${fcBuildSvgColor(
        backgroundColor,
        FcColorType.FILL,
        useCmyk
      )} ${fcBuildSvgColor(borderColor, FcColorType.STROKE, useCmyk)} stroke-width="${roundDecimal(
        borderSize
      )}" transform="rotate(45 ${roundDecimal(diamondOffset + diamondWidth / 2)} ${roundDecimal(
        diamondOffset + diamondWidth / 2
      )})" />`
      element += fcAddClipPath(idBase)
      svg.setClip(idBase)
      if (outlineColor && outlineWidth) {
        element += `<rect x="${roundDecimal(diamondOffset + outlineWidth / 2)}" y="${roundDecimal(
          diamondOffset + outlineWidth / 2
        )}" width="${roundDecimal(diamondWidth - outlineWidth)}" height="${roundDecimal(
          diamondWidth - outlineWidth
        )}" fill="none" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(outlineWidth)}" transform="rotate(45 ${roundDecimal(
          diamondOffset + diamondWidth / 2
        )} ${roundDecimal(diamondOffset + diamondWidth / 2)})" />`
        element += `<rect x="${diamondOffset + borderSize / 2 - outlineWidth / 2}" y="${
          diamondOffset + borderSize / 2 - outlineWidth / 2
        }" width="${diamondWidth - borderSize + outlineWidth}" height="${
          diamondWidth - borderSize + outlineWidth
        }" fill="none" ${fcBuildSvgColor(
          outlineColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${outlineWidth}" transform="rotate(45 ${
          diamondOffset + diamondWidth / 2
        } ${diamondOffset + diamondWidth / 2})" />`
      }
      break
    case FcShape.NONE:
      break
    case FcShape.CIRCLE:
    default:
      if (borderIntersections && borderIntersections.length > 0) {
        const len = borderIntersections.length
        const radius = w / 2 - borderSize / 2 - borderInset
        for (let i = 0; i < len; i++) {
          const inter = borderIntersections[i]
          let rads = inter[inter.length - 1]
          const startX = w / 2 + radius * Math.cos(rads)
          const startY = w / 2 + radius * Math.sin(rads)

          const interNext = borderIntersections[i === len - 1 ? 0 : i + 1]
          rads = interNext[0]
          const endX = w / 2 + radius * Math.cos(rads)
          const endY = w / 2 + radius * Math.sin(rads)

          if (outlineColor && outlineWidth) {
            element += `<path d="M ${roundDecimal(startX)} ${roundDecimal(startY)} A ${roundDecimal(
              radius
            )} ${roundDecimal(radius)} 0 0 1 ${roundDecimal(endX)} ${roundDecimal(
              endY
            )}" ${fcBuildSvgColor(
              outlineColor,
              FcColorType.STROKE,
              useCmyk
            )} stroke-width="${roundDecimal(
              borderSize
            )}" stroke-linecap="round" fill="none"></path>`
            element += `<path d="M ${roundDecimal(startX)} ${roundDecimal(startY)} A ${roundDecimal(
              radius
            )} ${roundDecimal(radius)} 0 0 1 ${roundDecimal(endX)} ${roundDecimal(
              endY
            )}" ${fcBuildSvgColor(
              borderColor,
              FcColorType.STROKE,
              useCmyk
            )} stroke-width="${roundDecimal(
              borderSize - outlineWidth * 2
            )}" stroke-linecap="round" fill="none"></path>`
          } else {
            element += `<path d="M ${roundDecimal(startX)} ${roundDecimal(startY)} A ${roundDecimal(
              radius
            )} ${roundDecimal(radius)} 0 0 1 ${roundDecimal(endX)} ${roundDecimal(
              endY
            )}" ${fcBuildSvgColor(
              borderColor,
              FcColorType.STROKE,
              useCmyk
            )} stroke-width="${roundDecimal(
              borderSize
            )}" stroke-linecap="round" fill="none"></path>`
          }
        }
      } else {
        element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(
          h / 2
        )}" r="${roundDecimal(
          w / 2 - borderSize / 2 - borderInset
        )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
          borderColor,
          FcColorType.STROKE,
          useCmyk
        )} stroke-width="${roundDecimal(borderSize)}" ${fcBuildSvgColor(
          backgroundColor,
          FcColorType.FILL,
          useCmyk
        )}/>`
        if (outlineColor && outlineWidth) {
          element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(
            h / 2
          )}" r="${roundDecimal(
            w / 2 - outlineWidth / 2 - borderInset
          )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width="${outlineWidth}" fill="none"/>`
          element += `<circle cx="${roundDecimal(w / 2)}" cy="${roundDecimal(
            h / 2
          )}" r="${roundDecimal(
            w / 2 - borderSize + outlineWidth / 2 - borderInset
          )}" shape-rendering='geometricPrecision' ${fcBuildSvgColor(
            outlineColor,
            FcColorType.STROKE,
            useCmyk
          )} stroke-width="${roundDecimal(outlineWidth)}" fill="none"/>`
        }
      }
      break
  }
  svg.addToDom(element)
}

export async function fcRenderContainerText(
  svg: FcSvg,
  idBase: string,
  container: FcContainer,
  reserved: FcReservedArea,
  noRender?: boolean,
  skipPrivacy?: boolean
): Promise<void> {
  if (!container.text) {
    return
  }
  let i = 0
  for (const text of container.text) {
    if (skipPrivacy && i === 0 && text.text.toLowerCase().indexOf('privacy') !== -1) {
      i++
      continue
    }
    const name = `conttext-${i}-${idBase}`
    await renderText(
      name,
      svg,
      text,
      container.shape,
      reserved,
      container.borderSize,
      container.borderInsetCalcValue,
      container.privacyTextPaddingHorizontal,
      container.privacyTextPaddingVertical,
      noRender
    )
    i++
  }
}

export async function fcRenderContainerMaskImage(
  svg: FcSvg,
  maskId: string,
  container: FcContainer
): Promise<void> {
  if (!container.maskImageUrl) {
    return
  }

  const imageData = await fcImageToTag(container.useCmyk, container.maskImageUrl)
  const containerImg = fcImageAddSize(
    imageData,
    0,
    0,
    svg.width,
    svg.height,
    '',
    undefined,
    undefined,
    maskId
  )
  svg.addToDom(containerImg)
}

async function renderContainerImage(
  svg: FcSvg,
  idBase: string,
  container: FcContainer
): Promise<void> {
  const opacity = container.containerImageUrlOpacity || 1
  if (container.containerImageUrl) {
    let color = ''
    if (container.containerImageUrlFillSvg) {
      color = fcBuildSvgColorForImageFill(container.borderColor, container.useCmyk)
    }
    const imageData = await fcImageToTag(container.useCmyk, container.containerImageUrl, color)
    const containerImg = fcImageAddSize(imageData, 0, 0, svg.width, svg.height, '', opacity)
    svg.addToDom(containerImg)
    return
  }
  if (!container.fragments) {
    return
  }
  let fragmentGradientColor: string | undefined = undefined
  if (container.fragmentGradient) {
    const label = `${idBase}fragGrad`
    const gradientDef = fcGenerateGradient(container.useCmyk, container.fragmentGradient, label)
    svg.addToDom(gradientDef)
    fragmentGradientColor = `url(#${label})`
  }
  const fragmentsLen = container.fragments.length
  const imagesArray: string[] = []
  const dimsArray: (FcArea | undefined)[] = []
  let maxW = 0
  let maxH = 0
  for (let i = 0; i < fragmentsLen; i++) {
    const fragment = container.fragments[i]
    const imageData = await fcImageToTag(container.useCmyk, fragment.imageUrl)
    imagesArray.push(imageData)
    const dims = fcGetSvgDimensions(imageData)
    dimsArray.push(dims)
    if (dims) {
      if (dims.width > maxW) {
        maxW = dims.width
      }
      if (dims.height > maxH) {
        maxH = dims.height
      }
    }
  }
  let scale = 1
  if (maxW > maxH) {
    scale = svg.width / maxW
  } else if (maxH) {
    scale = svg.height / maxH
  }

  for (let i = 0; i < fragmentsLen; i++) {
    const fragment = container.fragments[i]
    let innerFragmentGradientColor = fragmentGradientColor
    if (fragment.gradient) {
      const label = `${idBase}${i}fragGrad`
      const gradientDef = fcGenerateGradient(container.useCmyk, fragment.gradient, label)
      svg.addToDom(gradientDef)
      innerFragmentGradientColor = `url(#${label})`
    }
    let imageData = imagesArray[i]
    const changeColor = innerFragmentGradientColor || fragment.fillSvgColor
    if (changeColor) {
      imageData = fcChangeSvgColor(
        imageData,
        fcBuildSvgColorForImageFill(changeColor, container.useCmyk)
      )
    }
    let x = fragment.xpad || 0
    let y = fragment.ypad || 0
    let w = svg.width
    let h = svg.height
    const dims = dimsArray[i]
    if (dims) {
      w = dims.width * scale
      h = dims.height * scale
    }
    if (fragment.scalePercentage !== undefined) {
      w = (w * fragment.scalePercentage) / 100
      h = (h * fragment.scalePercentage) / 100
    }
    x += svg.width / 2 - w / 2
    y += svg.height / 2 - h / 2
    const containerImg = fcImageAddSize(imageData, x, y, w, h, '', fragment.opacity || 1)
    svg.addToDom(containerImg)
  }
}

export function fcAddClipPath(idBase: string): string {
  return `<defs>
                <clipPath id="${idBase}clipPath">
                    <use xlink:href="#${idBase}containerShape" />
                </clipPath>
            </defs>`
}
